import {
  ActionIcon, Alert, Badge, Box, Button, Card, Center, Checkbox, Code, Container, Group, Loader,
  Menu, Pagination, Progress, Stack, Table, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { notifications } from "@mantine/notifications";
import {
  IconUpload, IconAlertCircle, IconInfoCircle, IconCheck, IconTrash, IconArrowLeft,
  IconSearch, IconPlayerPlay, IconDownload, IconPaperclip, IconPencil,
} from "@tabler/icons-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { prefix, getAuthHeaders } from "../../../Boilerplate/api";
import {
  getImportSessions, getImportSession, getImportItems,
  updateImportItem, detectDuplicates, commitImport, deleteImportSession,
  contentDispositionAttachment,
} from "../api";
import { usePlan } from "../hooks/usePlan";
import { requestUpgrade } from "../Pricing/upgrade";

const statusColors = {
  parsing: "blue", review: "yellow", committing: "blue",
  done: "green", failed: "red",
};

const itemStatusColors = {
  parsed: "blue", skipped: "gray", duplicate: "orange",
  committed: "green", failed: "red",
};

// Treat blanks and the readpst "(null)" sentinel as empty. The parser now
// strips these, but older sessions parsed before that fix still hold them —
// normalize on load so the UI (and inline edits) never surface "(null)".
function blankSentinel(v) {
  if (typeof v !== "string") return v;
  const t = v.trim();
  return t === "" || t.toLowerCase() === "(null)" ? null : t;
}

function normalizeItem(item) {
  return {
    ...item,
    name: blankSentinel(item.name),
    title: blankSentinel(item.title),
    companyName: blankSentinel(item.companyName),
    email: blankSentinel(item.email),
    phone: blankSentinel(item.phone),
    location: blankSentinel(item.location),
  };
}

// ─── Upload a raw file (VCF or ZIP) using Content-Type, not FormData ─────
async function uploadImportFile(file) {
  const contentType = file.name.endsWith(".vcf") ? "text/vcard" : "application/zip";
  const resp = await fetch(`${prefix}/imports/upload`, {
    method: "POST",
    headers: {
      "Content-Type": contentType,
      "Content-Disposition": contentDispositionAttachment(file.name),
      ...getAuthHeaders(),
    },
    body: file,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || err.title || "Upload failed");
  }
  return resp.json();
}

// ─── Session list (landing) ──────────────────────────────────────────────
function ImportSessionList({ onSelect }) {
  const { t } = useTranslation("Veft");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { canBulkImport } = usePlan();

  useEffect(() => {
    getImportSessions().then((s) => setSessions(s ?? [])).finally(() => setLoading(false));
  }, []);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Bulk folder/ZIP import is a paid feature; VCF stays available on every plan.
    if (file.name.toLowerCase().endsWith(".zip") && !canBulkImport) {
      requestUpgrade(t("import_zip_upgrade"));
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const session = await uploadImportFile(file);
      const message = session.status === "parsing"
        ? t("import_parsing_background")
        : t("import_candidates_parsed", { n: session.totalItems });
      notifications.show({ title: t("import_uploaded"), message, color: "green" });
      onSelect(session.id);
    } catch (err) {
      notifications.show({ title: t("import_upload_failed"), message: err.message, color: "red" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) return <Center py="xl"><Loader /></Center>;

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>{t("import_title")}</Title>
        <Group gap="sm">
          <input
            type="file"
            ref={fileRef}
            style={{ display: "none" }}
            accept=".vcf,.zip"
            onChange={handleUpload}
          />
          <Button
            leftSection={<IconUpload size={16} />}
            loading={uploading}
            variant="light"
            onClick={() => fileRef.current?.click()}
          >
            {t("import_upload_button")}
          </Button>
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        {t("import_subtitle")}
      </Text>

      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={18} />}
        title={t("import_zip_help_title")}
      >
        <Stack gap={6}>
          <Text size="sm">
            <Trans i18nKey="import_zip_help_intro" t={t} components={{ b: <b /> }} />
          </Text>
          <Code block>{`candidates.zip
├─ Max Müller/
│  ├─ cv.pdf
│  ├─ photo.jpg        ${t("import_zip_tree_optional")}
│  └─ contact.vcf      ${t("import_zip_tree_contact")}
├─ Erika Mustermann/
│  └─ cv.pdf
└─ John Doe/
   └─ resume.docx`}</Code>
          <Text size="xs" c="dimmed">
            <Trans i18nKey="import_zip_help_footnote" t={t} components={{ b: <b /> }} />
          </Text>
        </Stack>
      </Alert>

      {sessions.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">{t("import_no_sessions")}</Text>
      ) : (
        <ResponsiveTable minWidth={640} highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("import_col_file")}</Table.Th>
              <Table.Th>{t("import_col_type")}</Table.Th>
              <Table.Th>{t("import_col_items")}</Table.Th>
              <Table.Th>{t("import_col_status")}</Table.Th>
              <Table.Th>{t("import_col_date")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sessions.map((s) => (
              <Table.Tr key={s.id} style={{ cursor: "pointer" }} onClick={() => onSelect(s.id)}>
                <Table.Td>
                  <Text size="sm" fw={500}>{s.originalFileName}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light"
                    color={s.sourceType === "graphContacts" ? "blue" : undefined}>
                    {s.sourceType === "vcf" ? "VCF" : s.sourceType === "graphContacts" ? "Outlook" : "ZIP"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{s.totalItems}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={statusColors[s.status] ?? "gray"}>
                    {t(`import_status_${s.status}`, { defaultValue: s.status })}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{new Date(s.createdAt).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" size="sm" color="red" onClick={(e) => {
                    e.stopPropagation();
                    deleteImportSession(s.id).then(() => {
                      setSessions((prev) => prev.filter((x) => x.id !== s.id));
                      notifications.show({ title: t("import_deleted"), color: "green" });
                    });
                  }}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </ResponsiveTable>
      )}
    </Stack>
    </Container>
  );
}

// ─── Editable cell (click-to-edit text) ──────────────────────────────────
function EditableCell({ value, field, sessionId, itemId, onUpdated, bold }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");

  // Items reload on poll, so re-sync from the latest value while not actively
  // editing — opening the editor should never show a stale value.
  useEffect(() => {
    if (!editing) setVal(value ?? "");
  }, [value, editing]);

  async function save() {
    setEditing(false);
    if (val === (value ?? "")) return;
    try {
      await updateImportItem(sessionId, itemId, { [field]: val || null });
      onUpdated(field, val || null);
    } catch {
      setVal(value ?? "");
    }
  }

  if (editing) {
    return (
      <TextInput
        size="xs"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); } }}
        autoFocus
        style={{ minWidth: 100 }}
      />
    );
  }

  return (
    <Text
      size="sm" fw={bold ? 600 : undefined} truncate
      style={{ cursor: "pointer", minHeight: 20 }}
      onClick={() => setEditing(true)}
      c={value ? undefined : "dimmed"}
    >
      {value || "—"}
    </Text>
  );
}

// ─── Labeled field editor (used in a row's expanded edit form) ────────────
function FieldEditor({ label, value, field, sessionId, itemId, onUpdated }) {
  const [val, setVal] = useState(value ?? "");
  useEffect(() => { setVal(value ?? ""); }, [value]);

  async function save() {
    // Normalize the same way loaded values are, so whitespace-only or "(null)"
    // input isn't re-persisted as a non-empty value.
    const normalized = blankSentinel(val);
    if ((normalized ?? "") === (value ?? "")) return;
    try {
      await updateImportItem(sessionId, itemId, { [field]: normalized });
      onUpdated(itemId, field, normalized);
    } catch {
      setVal(value ?? "");
    }
  }

  return (
    <TextInput
      size="xs" label={label} placeholder="—"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      w={160}
    />
  );
}

// ─── One candidate as a compact two-line row ──────────────────────────────
function ImportItemRow({ item, isReview, sessionId, onToggleSkip, onFieldUpdated }) {
  const { t } = useTranslation("Veft");
  const [editing, setEditing] = useState(false);
  const skipped = item.status === "skipped";
  const attachments = item.attachments ?? [];

  // Secondary line: title · company · location · email (drop phone, drop empties).
  const summaryParts = [item.title, item.companyName, item.location, item.email]
    .map((p) => p?.trim())
    .filter(Boolean);

  return (
    <Box
      py="xs"
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-2)",
        opacity: skipped ? 0.45 : 1,
      }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {isReview && (
          <Checkbox
            mt={3}
            checked={!skipped}
            onChange={() => onToggleSkip(item)}
            aria-label={t("import_aria_include")}
          />
        )}
        <Box style={{ flex: 1, minWidth: 0 }}>
          {/* Line 1: name + files + status (+ edit toggle) */}
          <Group gap="xs" wrap="nowrap" justify="space-between">
            <Box style={{ flex: 1, minWidth: 0 }}>
              {isReview ? (
                <EditableCell value={item.name} field="name" sessionId={sessionId} itemId={item.id}
                  bold onUpdated={(f, v) => onFieldUpdated(item.id, f, v)} />
              ) : (
                <Text size="sm" fw={600} truncate>{item.name || "—"}</Text>
              )}
            </Box>
            <Group gap="sm" wrap="nowrap">
              {attachments.length > 0 && (
                <Tooltip label={attachments.map((a) => a.originalFileName).join(", ")} multiline>
                  <Group gap={4} wrap="nowrap" c="dimmed">
                    <IconPaperclip size={14} />
                    <Text size="xs">{attachments.length}</Text>
                  </Group>
                </Tooltip>
              )}
              <Badge variant="dot" size="sm" color={itemStatusColors[item.status] ?? "gray"}>
                {t(`import_item_status_${item.status}`, { defaultValue: item.status })}
              </Badge>
              {isReview && (
                <ActionIcon
                  variant="subtle" size="sm" color={editing ? "blue" : "gray"}
                  onClick={() => setEditing((e) => !e)}
                  aria-label={t("import_aria_edit")}
                >
                  <IconPencil size={14} />
                </ActionIcon>
              )}
            </Group>
          </Group>

          {/* Line 2: read summary, or an inline edit form */}
          {editing && isReview ? (
            <Group gap="xs" mt={6} wrap="wrap" align="flex-end">
              <FieldEditor label={t("import_field_title")} value={item.title} field="title"
                sessionId={sessionId} itemId={item.id} onUpdated={onFieldUpdated} />
              <FieldEditor label={t("import_field_company")} value={item.companyName} field="companyName"
                sessionId={sessionId} itemId={item.id} onUpdated={onFieldUpdated} />
              <FieldEditor label={t("import_field_email")} value={item.email} field="email"
                sessionId={sessionId} itemId={item.id} onUpdated={onFieldUpdated} />
              <FieldEditor label={t("import_field_phone")} value={item.phone} field="phone"
                sessionId={sessionId} itemId={item.id} onUpdated={onFieldUpdated} />
              <FieldEditor label={t("import_field_location")} value={item.location} field="location"
                sessionId={sessionId} itemId={item.id} onUpdated={onFieldUpdated} />
            </Group>
          ) : (
            <Text size="xs" c="dimmed" truncate>
              {summaryParts.length > 0 ? summaryParts.join(" · ") : "—"}
            </Text>
          )}

          {/* Notes preview — surfaces the imported contact-card notes (LinkedIn,
              compensation, context) so they're visible before commit. */}
          {item.notes && (
            <Text size="xs" c="dimmed" mt={4} fs="italic" lineClamp={2}>
              {item.notes}
            </Text>
          )}
        </Box>
      </Group>
    </Box>
  );
}

// ─── Session review ──────────────────────────────────────────────────────
function ImportReview({ sessionId, onBack }) {
  const { t } = useTranslation("Veft");
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);
  const [duplicates, setDuplicates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;

  const loadSession = useCallback(() => getImportSession(sessionId).then(setSession), [sessionId]);

  const loadItems = useCallback(async () => {
    const result = await getImportItems(sessionId, page, pageSize);
    // The API returns a flat list (no pagination wrapper yet)
    const list = (Array.isArray(result) ? result : result?.items ?? []).map(normalizeItem);
    setItems(list);
    setTotalItems(Array.isArray(result) ? result.length : result?.totalCount ?? list.length);
  }, [sessionId, page]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSession(), loadItems()]).finally(() => setLoading(false));
  }, [loadSession, loadItems]);

  // Poll session status while parsing (Graph fetch) or committing
  useEffect(() => {
    const shouldPoll = polling || session?.status === "parsing";
    if (!shouldPoll) return;
    const interval = setInterval(async () => {
      const s = await getImportSession(sessionId);
      setSession(s);
      if (s.status === "review") {
        // Parsing complete (Graph fetch finished) — load items
        setPolling(false);
        loadItems();
        notifications.show({ title: t("import_contacts_fetched_title"), message: t("import_contacts_ready", { n: s.totalItems }), color: "green" });
      } else if (s.status === "done") {
        setPolling(false);
        setCommitting(false);
        loadItems();
        notifications.show({ title: t("import_complete_title"), message: t("import_candidates_imported", { n: s.committedItems }), color: "green" });
      } else if (s.status === "failed") {
        setPolling(false);
        setCommitting(false);
        loadItems();
        notifications.show({ title: t("import_failed_title"), message: s.errorMessage, color: "red" });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, session?.status, sessionId, loadItems, t]);

  async function handleDetectDuplicates() {
    try {
      const pairs = await detectDuplicates(sessionId);
      setDuplicates(pairs ?? []);
      if ((pairs ?? []).length === 0) {
        notifications.show({ title: t("import_no_duplicates_title"), message: t("import_no_duplicates_msg"), color: "green" });
      }
    } catch {
      notifications.show({ title: t("import_error"), message: t("import_dup_detection_failed"), color: "red" });
    }
  }

  async function handleCommit() {
    setCommitting(true);
    try {
      await commitImport(sessionId);
      setPolling(true);
      await loadSession();
    } catch (err) {
      setCommitting(false);
      notifications.show({ title: t("import_error"), message: t("import_start_failed"), color: "red" });
    }
  }

  async function handleToggleSkip(item) {
    const newSkip = item.status !== "skipped";
    try {
      await updateImportItem(sessionId, item.id, { skip: newSkip });
      setItems((prev) => prev.map((i) =>
        i.id === item.id ? { ...i, status: newSkip ? "skipped" : "parsed" } : i
      ));
    } catch {
      notifications.show({ title: t("import_error"), message: t("import_update_item_failed"), color: "red" });
    }
  }

  async function handleToggleAll(skip) {
    // Only act on items whose state would change to avoid unnecessary requests.
    const targets = items.filter((i) =>
      skip ? i.status === "parsed" : i.status === "skipped"
    );
    if (targets.length === 0) return;

    try {
      await Promise.all(targets.map((i) => updateImportItem(sessionId, i.id, { skip })));
      const targetIds = new Set(targets.map((i) => i.id));
      setItems((prev) => prev.map((i) =>
        targetIds.has(i.id) ? { ...i, status: skip ? "skipped" : "parsed" } : i
      ));
    } catch {
      notifications.show({ title: t("import_error"), message: t("import_update_items_failed"), color: "red" });
    }
  }

  function handleFieldUpdated(itemId, field, value) {
    setItems((prev) => prev.map((i) =>
      i.id === itemId ? { ...i, [field]: value } : i
    ));
  }

  if (loading) return <Center py="xl"><Loader /></Center>;
  if (!session) return <Text c="red">{t("import_session_not_found")}</Text>;

  const isReview = session.status === "review";
  const isDone = session.status === "done";
  const isCommitting = session.status === "committing";
  const parsedCount = items.filter((i) => i.status === "parsed").length;
  const skippedCount = items.filter((i) => i.status === "skipped").length;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Build a lookup of items by ID for duplicate display
  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group>
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          {t("import_back")}
        </Button>
        <Title order={3}>{session.originalFileName}</Title>
        <Badge color={statusColors[session.status] ?? "gray"}>
          {t(`import_status_${session.status}`, { defaultValue: session.status })}
        </Badge>
      </Group>

      {/* Summary stats */}
      <Group gap="lg">
        <Text size="sm"><b>{session.totalItems}</b> {t("import_stat_total")}</Text>
        {isDone && (
          <>
            <Text size="sm" c="green"><b>{session.committedItems}</b> {t("import_stat_imported")}</Text>
            <Text size="sm" c="gray"><b>{session.skippedItems}</b> {t("import_stat_skipped")}</Text>
            <Text size="sm" c="orange"><b>{session.duplicateItems}</b> {t("import_stat_duplicates")}</Text>
          </>
        )}
        {isReview && (
          <>
            <Text size="sm" c="blue"><b>{parsedCount}</b> {t("import_stat_ready")}</Text>
            <Text size="sm" c="gray"><b>{skippedCount}</b> {t("import_stat_skipped")}</Text>
          </>
        )}
      </Group>

      {/* Outlook attachment diagnostics — visible after the fetch pass so the
          user can see "did we even get any attachments back from Graph?" */}
      {session.sourceType === "graphContacts" && (isReview || isDone) && (
        <Group gap="lg">
          <Text size="sm" c="dimmed">
            {t("import_graph_attachments", {
              fetched: session.graphAttachmentsFetched ?? 0,
              contacts: session.graphContactsWithAttachments ?? 0,
            })}
            {(session.graphAttachmentFetchErrors ?? 0) > 0 && (
              <Text span c="red">{t("import_graph_attachment_errors", { errors: session.graphAttachmentFetchErrors })}</Text>
            )}
          </Text>
        </Group>
      )}

      {/* Parsing/fetching progress (Graph import) */}
      {session.status === "parsing" && (
        <Alert color="blue" icon={<Loader size={16} />}>
          {session.sourceType === "zipFolder"
            ? t("import_parsing_zip")
            : t("import_parsing_graph")}
          <Progress value={100} animated mt="xs" />
        </Alert>
      )}

      {/* Committing progress */}
      {isCommitting && (
        <Alert color="blue" icon={<Loader size={16} />}>
          {t("import_committing")}
          <Progress value={100} animated mt="xs" />
        </Alert>
      )}

      {/* Done alert */}
      {isDone && (
        <Alert color="green" icon={<IconCheck size={16} />}>
          {t("import_completed_msg", { n: session.committedItems })}
        </Alert>
      )}

      {/* Duplicate detection results */}
      {duplicates && duplicates.length > 0 && isReview && (
        <Alert color="orange" icon={<IconAlertCircle size={16} />} title={t("import_duplicates_found", { n: duplicates.length })}>
          <Stack gap="xs" mt="xs">
            {duplicates.map((d, i) => {
              const item = itemMap[d.itemId];
              return (
                <Group key={i} gap="xs">
                  <Text size="xs" fw={500}>{item?.name ?? d.itemId}</Text>
                  <Text size="xs" c="dimmed">
                    {d.existingCandidateName
                      ? t("import_matches_existing", { name: d.existingCandidateName })
                      : t("import_matches_item", { name: itemMap[d.matchItemId]?.name ?? d.matchItemId })}
                  </Text>
                  <Badge size="xs" variant="light">{Math.round(d.confidence * 100)}%</Badge>
                  <Text size="xs" c="dimmed">{d.reason}</Text>
                </Group>
              );
            })}
          </Stack>
        </Alert>
      )}

      {/* Actions */}
      {isReview && (
        <Group>
          <Button
            leftSection={<IconSearch size={16} />}
            variant="light"
            onClick={handleDetectDuplicates}
          >
            {t("import_check_duplicates")}
          </Button>
          <Button
            leftSection={<IconPlayerPlay size={16} />}
            onClick={handleCommit}
            loading={committing}
            disabled={parsedCount === 0}
          >
            {t("import_button_import_n", { n: parsedCount })}
          </Button>
        </Group>
      )}

      {/* Items list — compact two-line rows */}
      <Stack gap={0}>
        {isReview && (() => {
          const togglable = items.filter((i) => i.status === "parsed" || i.status === "skipped");
          const includedCount = togglable.filter((i) => i.status !== "skipped").length;
          const allIncluded = togglable.length > 0 && includedCount === togglable.length;
          const noneIncluded = includedCount === 0;
          return (
            <Group gap="xs" pb="xs">
              <Checkbox
                size="xs"
                aria-label={t("import_aria_toggle_all")}
                checked={allIncluded}
                indeterminate={!allIncluded && !noneIncluded}
                disabled={togglable.length === 0}
                onChange={() => handleToggleAll(allIncluded)}
                label={<Text size="xs" c="dimmed">{t("import_select_all")}</Text>}
              />
            </Group>
          );
        })()}

        {items.map((item) => (
          <ImportItemRow
            key={item.id}
            item={item}
            isReview={isReview}
            sessionId={sessionId}
            onToggleSkip={handleToggleSkip}
            onFieldUpdated={handleFieldUpdated}
          />
        ))}
      </Stack>

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </Stack>
    </Container>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────
export default function ImportPage() {
  const { t } = useTranslation("Veft");
  const [selectedSession, setSelectedSession] = useState(null);
  const [, setLocation] = useLocation();
  const searchString = useSearch();

  // Handle ?session= param from OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const sessionParam = params.get("session");
    if (sessionParam && !selectedSession) {
      setSelectedSession(sessionParam);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
    const error = params.get("error");
    if (error) {
      notifications.show({ title: t("import_error_title"), message: error.replace(/_/g, " "), color: "red" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchString]);

  if (selectedSession) {
    return (
      <ImportReview
        sessionId={selectedSession}
        onBack={() => setSelectedSession(null)}
      />
    );
  }

  return <ImportSessionList onSelect={setSelectedSession} />;
}
